import { Controller } from "@hotwired/stimulus"
import { showBlock } from "lib/helpers"

// Manages the conversation tester widget (right column) in the assistant
// edit views. Handles: send/receive messages, streaming, conversation
// history, widget sizing/scrolling, audio feature toggles.
//
// Usage:
//   .right_col{ data: { controller: "assistants--conversation-tester" } }
export default class extends Controller {
  static values = {
    avatarSrc: String,
    waitMessageGifSrc: String,
    defaultWidgetButtonLogo: String,
    refreshSystemMessagePath: String,
    applicationId: String,
    assistantMetricsPath: String,
    errorBlock: { type: String, default: "#chatbot-errors" },
  }

  connect() {
    this.messageOrder = 0
    this.listeningForNewMessages = false
    this.waitingForWorkflowInput = false
    this.streamingMessageElement = null
    this.streamingMsgEl = null
    this.streamingMessageText = ''
    this.streamingChunks = new Map()
    this.nextExpectedChunkOrder = 1
    this.streamingFirstWordDone = false
    this.skipStreamingChunks = false
    this.streamingDisplayedLength = 0
    this.typingTimer = null
    this.typingCharsPerTick = 2
    this.typingIntervalMs = 15
    this.marked = window.marked
    this.purify = window.DOMPurify

    this.boundSetWidgetSize = () => this.setWidgetSize()
    this.boundTestWidgetScroll = () => this.updateTestWidgetPosition()

    this.initMarked()
    this.clearConversation()
    this.initChatbotSendMessage()
    this.initChatbotAutoResizeTextarea()
    this.putHistoryToConversation()
    this.resumeStreamingIfNeeded()
    this.initWidgetLogoButtonHandler()
    this.initHandleSettingsBtnClick()
    this.initHandleAudioFeatureBtnClick()
    this.initTestWidgetScrollHandler()
    window.addEventListener('resize', this.boundSetWidgetSize)
    this.setWidgetSize()
  }

  disconnect() {
    if (this.typingTimer) { clearInterval(this.typingTimer); this.typingTimer = null }
    window.removeEventListener('resize', this.boundSetWidgetSize)
    window.removeEventListener('scroll', this.boundTestWidgetScroll)
    $(this.element).off(".convTester")
    $("#chatbot_send_message").off(".convTester")
    $(this.chatbotMessage).off(".convTester")
    $('#settings-btn').off(".convTester")
    $('#audio-features').off(".convTester")
    this.listeningForNewMessages = false
    this.waitingForWorkflowInput = false
  }

  get chatbotMessage() { return document.getElementById('chatbot_message') }
  get conversationBody() { return this.element.querySelector('.conversation-body') }

  initMarked() {
    if (!this.marked) return
    this.marked.setOptions({
      gfm: true, breaks: true, headerIds: true, mangle: false,
      highlight(code, lang) {
        try {
          if (lang && hljs.getLanguage(lang)) return hljs.highlight(code, { language: lang }).value
          return hljs.highlightAuto(code).value
        } catch (_) { return code }
      }
    })
    const renderer = new this.marked.Renderer()
    renderer.link = function(href, title, text) {
      const titleAttr = title ? ` title="${title}"` : ''
      return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`
    }
    this.marked.setOptions({ renderer })
  }

  clearConversation() {
    const self = this
    $(this.element).on('click.convTester', '.chatbot-interactive-reset', function (e) {
      e.preventDefault()
      if ($(this).hasClass("disabled")) return
      self.listeningForNewMessages = false
      self.waitingForWorkflowInput = false
      $(self.conversationBody).empty()
      const convId = self.getConversationId()
      window.sessionStorage.removeItem(convId)
      window.sessionStorage.removeItem(convId + "_streaming")
      window.sessionStorage.removeItem(convId + "_streaming_buffer")
      window.sessionStorage.removeItem(convId + "_streaming_chunk_order")
      window.sessionStorage.removeItem("conversation_id")
      self.enableSendMessage()
      self.refreshSystemMessage()
      const botMsg = self.chatbotMessage
      if (botMsg && botMsg.dataset.assistantType === 'advanced') {
        self.triggerWorkflowStart()
      }
    })
  }

  triggerWorkflowStart() {
    const botMsg = this.chatbotMessage
    if (!botMsg) return
    const url = botMsg.dataset.url
    const botId = botMsg.dataset.botId
    const botUserId = botMsg.dataset.botUserId
    const self = this
    this.addWaitMessage()
    this.disableSendMessage()
    const convId = this.getConversationId()
    $.ajax({
      url, method: "POST", dataType: "json", contentType: 'application/json',
      data: JSON.stringify({ conversation_id: convId, message: ".", history: [], bot_user_id: botUserId, bot_id: botId, workflow_trigger: true }),
      success(response) {
        if (response.no_workflow) { self.enableSendMessage(); self.removeWaitMessage() }
        else if (response.success) { self.listeningForNewMessages = true; self.resumeReceiveMessage(url) }
        else { self.enableSendMessage(); self.removeWaitMessage() }
      },
      error() { self.enableSendMessage(); self.removeWaitMessage() }
    })
  }

  disableSendMessage() {
    $("#chatbot_send_message").attr("disabled", true).addClass("disabled")
    $('#chatbot_message').attr("disabled", true).addClass("disabled")
    $('#chatbot_reset_message').attr("disabled", true).addClass("disabled")
    $(".chatbot-interactive-reset").attr("disabled", true).addClass("disabled")
    $(".chatbot-interactive-voice").attr("disabled", true).addClass("disabled")
    $(".conversation-control").addClass("disabled")
  }

  enableSendMessage() {
    $("#chatbot_send_message").attr("disabled", false).removeClass("disabled")
    $("#chatbot_reset_message").attr("disabled", false).removeClass("disabled")
    $('#chatbot_message').attr("disabled", false).removeClass("disabled").focus()
    $(".chatbot-interactive-reset").attr("disabled", false).removeClass("disabled")
    $(".chatbot-interactive-voice").attr("disabled", false).removeClass("disabled")
    $(".conversation-control").removeClass("disabled")
  }

  initChatbotAutoResizeTextarea() {
    const textarea = this.chatbotMessage
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.overflowY = 'hidden'
    const autoResize = () => {
      const maxHeight = 114, minHeight = 19
      if (textarea.value.trim() === '') { textarea.style.height = minHeight + 'px'; textarea.style.overflowY = 'hidden'; textarea.classList.remove('scrolling'); return }
      textarea.style.height = 'auto'
      const scrollHeight = textarea.scrollHeight
      if (scrollHeight <= maxHeight) { textarea.style.height = Math.max(scrollHeight, minHeight) + 'px'; textarea.style.overflowY = 'hidden'; textarea.classList.remove('scrolling') }
      else { textarea.style.height = maxHeight + 'px'; textarea.style.overflowY = 'auto'; textarea.classList.add('scrolling') }
    }
    textarea.addEventListener('input', autoResize)
    textarea.addEventListener('paste', () => setTimeout(autoResize, 10))
    autoResize()
  }

  initChatbotSendMessage() {
    const self = this
    $("#chatbot_send_message").on('click.convTester', function (e) {
      e.preventDefault()
      if ($(this).hasClass("disabled")) return
      const botMsg = $(self.chatbotMessage)
      botMsg.focus()
      const message = botMsg.val()
      const url = botMsg.data('url')
      const botUserId = botMsg.data('botUserId')
      const botId = botMsg.data('botId')
      if (!message || message.trim() === "") return
      const skipBlocking = self.waitingForWorkflowInput
      self.waitingForWorkflowInput = false
      self.addOutgoingMessage(message)
      if (!skipBlocking) self.disableSendMessage()
      self.sendMessage(url, botId, botUserId, message, null, skipBlocking)
      botMsg.val('')
      botMsg[0].style.height = 'auto'
      botMsg[0].style.overflowY = 'hidden'
      botMsg[0].classList.remove('scrolling')
    })
    $(this.chatbotMessage).on('keydown.convTester', function (event) {
      if ((event.which === 13 || event.keyCode === 13) && !event.shiftKey) {
        event.preventDefault()
        if (!$(this).hasClass("disabled")) $("#chatbot_send_message").click()
      }
    })
  }

  refreshSystemMessage() {
    if (!this.refreshSystemMessagePathValue) return
    $.ajax({ url: this.refreshSystemMessagePathValue, method: "POST", dataType: "json", contentType: 'application/json',
      success(response) { if (!response.success) console.error("Failed to refresh system message: " + response.message) },
      error() { console.error("An error occurred while refreshing the system message.") }
    })
  }

  sendMessage(url, botId, botUserId, message, attachments, skipBlocking, displayMessage) {
    const self = this
    const clientTimestamp = new Date().getTime()
    let waitMessageId = null
    if (!skipBlocking) waitMessageId = this.addWaitMessage()
    const transaction = window.elasticApm.startTransaction('send_sca_message', 'custom', { managed: false })
    const transactionQBID = this.uuid()
    transaction.addLabels({ assistant_id: botId, transaction_qb_id: transactionQBID, application_id: this.applicationIdValue })
    const scaSpan = transaction.startSpan('send_sca_message', 'custom')
    const payload = { conversation_id: this.getConversationId(), message, history: this.getMessagesHistory(), bot_user_id: botUserId, bot_id: botId }
    if (attachments && attachments.length > 0) payload.attachments = attachments
    $.ajax({
      url, method: "POST", dataType: "json", contentType: 'application/json',
      headers: { 'Transaction-qb-id': transactionQBID },
      data: JSON.stringify(payload),
      success(response) {
        if (response.success) {
          self.addMessageToHistory(displayMessage != null ? displayMessage : message, null, 'outgoing')
          scaSpan.end(); transaction.end()
          if (skipBlocking) { self.enableSendMessage(); self.removeWaitMessage() }
          self.initAssistantStatistic()
          if (!self.listeningForNewMessages) {
            self.listeningForNewMessages = true
            self.receiveMessage(url, waitMessageId, response.request_timestamp, clientTimestamp, transaction, scaSpan, transactionQBID)
          }
        } else {
          scaSpan.end(); transaction.end(); self.enableSendMessage(); self.removeWaitMessage()
          showBlock(self.errorBlockValue, response.message)
        }
      },
      error() { scaSpan.end(); transaction.end(); self.enableSendMessage(); self.removeWaitMessage(); showBlock(self.errorBlockValue, "An error occurred during request") }
    })
  }

  receiveMessage(url, waitMessageId, requestTimestamp, clientTimestamp, transaction, scaSpan, transactionQBID) {
    const self = this
    $.ajax({
      url: url + "/" + this.getConversationId(), method: "GET", dataType: "json",
      headers: { 'Transaction-qb-id': transactionQBID },
      data: { request_timestamp: requestTimestamp },
      success(response) {
        if (response.success) {
          if (response.retry_attempt) {
            self.receiveMessage(url, waitMessageId, response.request_timestamp, clientTimestamp, transaction, scaSpan, transactionQBID)
          } else if (response.message && response.ai_stream_event === 'chunk') {
            self.handleStreamingChunk(response)
            self.receiveMessage(url, waitMessageId, response.request_timestamp, clientTimestamp, transaction, scaSpan, transactionQBID)
          } else {
            self.cleanupStreamingMessage()
            scaSpan.end(); transaction.end(); self.enableSendMessage(); self.removeWaitMessage()
            let messageText = (response.message && response.message.message != null) ? response.message.message : response.message
            let markedMsg = self.parseAndSanitize(messageText)
            self.addIncomingMessage(markedMsg, response.sources, response.options, response.node_type, response.input_request)
            self.addMessageToHistory(markedMsg, response.sources, 'incoming', response.options, response.node_type, response.input_request)
            if (response.options && response.options.length > 0) {
              self.disableSendMessage()
              $(".chatbot-interactive-reset").attr("disabled", false).removeClass("disabled")
              $(".conversation-control").removeClass("disabled")
            }
            if (response.input_request) self.waitingForWorkflowInput = true
            self.initAssistantStatistic()
            self.sendMessageRequestMetrics(new Date().getTime() - clientTimestamp)
            self.receiveMessage(url, waitMessageId, response.request_timestamp, clientTimestamp, transaction, scaSpan, transactionQBID)
          }
        } else { scaSpan.end(); transaction.end(); showBlock(self.errorBlockValue, response.message) }
      },
      error() { scaSpan.end(); transaction.end(); showBlock(self.errorBlockValue, "An error occurred during request") }
    })
  }

  handleStreamingChunk(response) {
    const chunkText = response.message
    if (!chunkText || this.skipStreamingChunks) return
    const chunkOrder = parseInt(response.ai_stream_chunk_order, 10)
    if (isNaN(chunkOrder)) { this.streamingMessageText += chunkText }
    else {
      this.streamingChunks.set(chunkOrder, chunkText)
      while (this.streamingChunks.has(this.nextExpectedChunkOrder)) {
        this.streamingMessageText += this.streamingChunks.get(this.nextExpectedChunkOrder)
        this.streamingChunks.delete(this.nextExpectedChunkOrder)
        this.nextExpectedChunkOrder++
      }
    }
    if (!this.streamingFirstWordDone) {
      const idx = this.streamingMessageText.search(/\s/)
      if (idx === -1) return
      let firstWord = this.streamingMessageText.substring(0, idx)
      if (firstWord.includes('<IDK>')) { this.skipStreamingChunks = true; this.cleanupStreamingMessage(); return }
      firstWord = firstWord.replace(/<STK>/g, '')
      this.streamingMessageText = (firstWord + this.streamingMessageText.substring(idx)).trimStart()
      this.streamingFirstWordDone = true
    }
    this.removeWaitMessage()
    if (!this.streamingMessageElement) {
      const messageId = this.addIncomingMessage('\u200B', null)
      if (messageId) {
        this.streamingMessageElement = document.getElementById(messageId)
        this.streamingMsgEl = this.streamingMessageElement.querySelector('.msg')
      }
    }
    this.startTypingTimer()
    const convId = this.getConversationId()
    window.sessionStorage.setItem(convId + "_streaming", "true")
    window.sessionStorage.setItem(convId + "_streaming_buffer", this.streamingMessageText)
    window.sessionStorage.setItem(convId + "_streaming_chunk_order", this.nextExpectedChunkOrder.toString())
  }

  startTypingTimer() {
    if (this.typingTimer) return
    const self = this
    this.typingTimer = setInterval(function () {
      if (self.streamingDisplayedLength >= self.streamingMessageText.length) { clearInterval(self.typingTimer); self.typingTimer = null; return }
      self.streamingDisplayedLength = Math.min(self.streamingDisplayedLength + self.typingCharsPerTick, self.streamingMessageText.length)
      const displayText = self.streamingMessageText.substring(0, self.streamingDisplayedLength)
      const html = self.parseAndSanitize(displayText)
      if (self.streamingMsgEl) self.streamingMsgEl.innerHTML = html
    }, this.typingIntervalMs)
  }

  cleanupStreamingMessage() {
    if (this.typingTimer) { clearInterval(this.typingTimer); this.typingTimer = null }
    this.streamingDisplayedLength = 0
    if (this.streamingMessageElement) { this.streamingMessageElement.remove(); this.streamingMessageElement = null; this.streamingMsgEl = null; this.streamingMessageText = '' }
    this.streamingChunks.clear()
    this.nextExpectedChunkOrder = 1
    this.streamingFirstWordDone = false
    this.skipStreamingChunks = false
    const convId = this.getConversationId()
    window.sessionStorage.removeItem(convId + "_streaming")
    window.sessionStorage.removeItem(convId + "_streaming_buffer")
    window.sessionStorage.removeItem(convId + "_streaming_chunk_order")
  }

  resumeStreamingIfNeeded() {
    const convId = this.getConversationId()
    if (window.sessionStorage.getItem(convId + "_streaming") !== "true") return
    const bufferedText = window.sessionStorage.getItem(convId + "_streaming_buffer")
    if (bufferedText) {
      this.streamingMessageText = bufferedText
      this.streamingFirstWordDone = true
      const savedOrder = parseInt(window.sessionStorage.getItem(convId + "_streaming_chunk_order"), 10)
      if (!isNaN(savedOrder)) this.nextExpectedChunkOrder = savedOrder + 1
      const markedMsg = this.parseAndSanitize(bufferedText)
      const messageId = this.addIncomingMessage(markedMsg, null)
      if (messageId) { this.streamingMessageElement = document.getElementById(messageId); this.streamingMsgEl = this.streamingMessageElement.querySelector('.msg') }
      this.streamingDisplayedLength = this.streamingMessageText.length
    } else { this.addWaitMessage() }
    this.disableSendMessage()
    this.listeningForNewMessages = true
    const botMsg = this.chatbotMessage
    if (botMsg) this.resumeReceiveMessage(botMsg.dataset.url)
  }

  resumeReceiveMessage(url) {
    const self = this
    $.ajax({
      url: url + "/" + this.getConversationId(), method: "GET", dataType: "json", data: {},
      success(response) {
        if (response.success) {
          if (response.retry_attempt) { self.resumeReceiveMessage(url) }
          else if (response.message && response.ai_stream_event === 'chunk') { self.handleStreamingChunk(response); self.resumeReceiveMessage(url) }
          else {
            self.cleanupStreamingMessage(); self.skipStreamingChunks = false; self.enableSendMessage(); self.removeWaitMessage()
            let messageText = (response.message && response.message.message != null) ? response.message.message : response.message
            let markedMsg = self.parseAndSanitize(messageText)
            self.addIncomingMessage(markedMsg, response.sources, response.options, response.node_type, response.input_request)
            self.addMessageToHistory(markedMsg, response.sources, 'incoming', response.options, response.node_type, response.input_request)
            if (response.options && response.options.length > 0) {
              self.disableSendMessage()
              $(".chatbot-interactive-reset").attr("disabled", false).removeClass("disabled")
              $(".conversation-control").removeClass("disabled")
            }
            if (response.input_request) self.waitingForWorkflowInput = true
            self.initAssistantStatistic()
            self.resumeReceiveMessage(url)
          }
        } else { showBlock(self.errorBlockValue, response.message) }
      },
      error() { showBlock(self.errorBlockValue, "An error occurred during request") }
    })
  }

  sendMessageRequestMetrics(duration) {
    if (!this.assistantMetricsPathValue) return
    $.ajax({ url: this.assistantMetricsPathValue, method: "POST", dataType: "json", contentType: "application/json", data: JSON.stringify({ duration }) })
  }

  initAssistantStatistic() {
    const el = document.getElementById("unanswered-question-chart")
    if (!el || !window.page_args || !window.page_args.assistant_statistic_path) return
    $.ajax({
      url: window.page_args.assistant_statistic_path, type: "GET", dataType: "JSON",
      success(response) {
        if (response.success) {
          $("#total-received-messages").html(response.data.total_received_messages)
          $("#not-answered-percent").html(response.data.percentage_not_answered + "%")
        }
      }
    })
  }

  // ---- Message rendering ----

  addWaitMessage() {
    const el = document.createElement('span')
    el.classList.add("wait_message")
    const image = document.createElement("img")
    image.src = this.waitMessageGifSrcValue
    image.style.display = 'block'
    el.appendChild(image)
    return this.addIncomingMessage(el, null)
  }

  removeWaitMessage() { $('.wait_message').parents(".incoming").remove() }

  addOutgoingMessage(message) {
    const msg = document.createElement('div')
    msg.classList.add('msg')
    msg.textContent = message
    const outgoing = document.createElement('div')
    outgoing.dataset.order = this.getMessageOrderNumber()
    outgoing.classList.add('outgoing')
    outgoing.appendChild(msg)
    $(this.conversationBody).prepend(outgoing)
    this.updateWidgetColorPreview()
  }

  addIncomingMessage(message, sources, options, nodeType, inputRequest) {
    if (message == null || message === "") return
    let avatarSrc = this.avatarSrcValue
    const currentAvatar = document.getElementById('current-avatar')
    if (currentAvatar) avatarSrc = currentAvatar.src

    const iconChatbot = document.createElement('img')
    iconChatbot.classList.add('current-avatar-small', 'avatar')
    iconChatbot.src = avatarSrc

    const avatar = document.createElement('div')
    avatar.classList.add('avatar')
    avatar.appendChild(iconChatbot)

    const incomingMessageBody = document.createElement('div')
    incomingMessageBody.classList.add('msg')

    if (message instanceof Element) { incomingMessageBody.appendChild(message) }
    else if (/<[^>]*>/g.test(message)) { const temp = document.createElement('div'); temp.innerHTML = message; const frag = document.createDocumentFragment(); while (temp.firstChild) frag.appendChild(temp.firstChild); incomingMessageBody.appendChild(frag) }
    else { incomingMessageBody.appendChild(this.convertNewlinesToTextNodes(message)) }

    const incomingMessage = document.createElement('div')
    incomingMessage.classList.add('m-body')
    incomingMessage.appendChild(incomingMessageBody)

    if (sources && sources.length > 0) this.appendSources(incomingMessageBody, sources)
    if (options && options.length > 0) this.appendOptions(incomingMessageBody, options, nodeType)
    if (nodeType === 'ask_date' && inputRequest) this.appendDatePicker(incomingMessageBody, inputRequest)
    if (nodeType === 'ask_file') this.appendFilePicker(incomingMessageBody, inputRequest)

    const divIncoming = document.createElement('div')
    divIncoming.dataset.order = this.getMessageOrderNumber()
    divIncoming.id = "message-" + this.uuid()
    divIncoming.classList.add('incoming')
    divIncoming.appendChild(avatar)
    divIncoming.appendChild(incomingMessage)
    $(this.conversationBody).prepend(divIncoming)
    return divIncoming.id
  }

  appendSources(parent, sources) {
    const titleContainer = document.createElement('div')
    titleContainer.classList.add('sources-list-title')
    const icon = document.createElement('i'); icon.classList.add('icon_arrow')
    titleContainer.appendChild(icon)
    const titleText = document.createElement('p'); titleText.textContent = 'References'
    titleContainer.appendChild(titleText)
    titleContainer.addEventListener('click', function(e) {
      const sourcesList = e.currentTarget.nextSibling
      e.currentTarget.classList.toggle('show_list')
      sourcesList.classList.toggle('show_list')
      sourcesList.style.maxHeight = sourcesList.style.maxHeight ? null : sourcesList.scrollHeight + "px"
    })
    parent.appendChild(titleContainer)

    const sourcesList = document.createElement('div')
    sourcesList.classList.add('sources-list')
    for (const source of sources) {
      const item = document.createElement('div'); item.classList.add('source-item')
      if (source.url) { const link = document.createElement('a'); link.href = source.url; link.target = '_blank'; link.textContent = source.title; item.appendChild(link) }
      else { const p = document.createElement('p'); p.textContent = source.title; item.appendChild(p) }
      sourcesList.appendChild(item)
    }
    parent.appendChild(sourcesList)
  }

  appendOptions(parent, options, nodeType) {
    const self = this
    if (nodeType === 'multiple_choice') {
      const container = document.createElement('div'); container.classList.add('multiple-choice-options')
      const selectedIds = new Set()
      for (const option of options) {
        const row = document.createElement('div'); row.classList.add('multiple-choice-option'); row.dataset.optionId = option.id
        const checkbox = document.createElement('span'); checkbox.classList.add('multiple-choice-checkbox'); checkbox.innerHTML = this.uncheckedCheckboxSvg()
        const label = document.createElement('span'); label.classList.add('multiple-choice-label'); label.textContent = option.label
        row.appendChild(checkbox); row.appendChild(label)
        row.addEventListener('click', function() {
          if (row.classList.contains('disabled')) return
          if (selectedIds.has(option.id)) { selectedIds.delete(option.id); checkbox.innerHTML = self.uncheckedCheckboxSvg(); row.classList.remove('checked') }
          else { selectedIds.add(option.id); checkbox.innerHTML = self.checkedCheckboxSvg(); row.classList.add('checked') }
        })
        container.appendChild(row)
      }
      const submitBtn = document.createElement('div'); submitBtn.classList.add('multiple-choice-submit'); submitBtn.textContent = 'Submit'
      submitBtn.addEventListener('click', function() {
        if (submitBtn.classList.contains('disabled') || selectedIds.size === 0) return
        container.querySelectorAll('.multiple-choice-option').forEach(r => r.classList.add('disabled'))
        submitBtn.classList.add('disabled')
        self.handleMultipleChoiceSubmit(Array.from(selectedIds), options)
      })
      container.appendChild(submitBtn)
      parent.appendChild(container)
    } else {
      const container = document.createElement('div'); container.classList.add('single-choice-options')
      for (const option of options) {
        const btn = document.createElement('div'); btn.classList.add('single-choice-option')
        btn.dataset.optionId = option.id; btn.dataset.optionLabel = option.label; btn.textContent = option.label
        btn.addEventListener('click', () => self.handleOptionClick(option, container))
        container.appendChild(btn)
      }
      parent.appendChild(container)
    }
  }

  appendDatePicker(parent, inputRequest) {
    const self = this
    const constraints = inputRequest.constraints || {}
    const includeTime = constraints.include_time === true
    const dateContainer = document.createElement('div'); dateContainer.classList.add('ask-date-picker')
    if (!includeTime) {
      const btn = document.createElement('div'); btn.classList.add('ask-date-btn'); btn.textContent = 'Select a date'
      const dateInput = document.createElement('input'); dateInput.type = 'date'; dateInput.classList.add('ask-date-overlay')
      if (constraints.min) dateInput.min = constraints.min
      if (constraints.max) dateInput.max = constraints.max
      dateInput.addEventListener('change', function() { if (!dateInput.value) return; btn.textContent = dateInput.value; dateContainer.classList.add('disabled'); self.handleDateSubmit(dateInput.value) })
      dateContainer.appendChild(btn); dateContainer.appendChild(dateInput)
    } else {
      const btn = document.createElement('div'); btn.classList.add('ask-date-btn'); btn.textContent = 'Select date & time'
      const inputRow = document.createElement('div'); inputRow.classList.add('ask-datetime-row'); inputRow.style.display = 'none'
      const dateInput = document.createElement('input'); dateInput.type = 'datetime-local'; dateInput.classList.add('ask-datetime-input')
      if (constraints.min) dateInput.min = constraints.min + 'T00:00'
      if (constraints.max) dateInput.max = constraints.max + 'T23:59'
      const confirmBtn = document.createElement('div'); confirmBtn.classList.add('ask-date-btn', 'ask-datetime-confirm'); confirmBtn.textContent = 'Confirm'
      btn.addEventListener('click', () => { btn.style.display = 'none'; inputRow.style.display = 'flex'; dateInput.focus() })
      confirmBtn.addEventListener('click', () => { if (!dateInput.value) return; inputRow.style.display = 'none'; btn.style.display = ''; btn.textContent = dateInput.value; dateContainer.classList.add('disabled'); self.handleDateSubmit(dateInput.value) })
      inputRow.appendChild(dateInput); inputRow.appendChild(confirmBtn)
      dateContainer.appendChild(btn); dateContainer.appendChild(inputRow)
    }
    parent.appendChild(dateContainer)
  }

  appendFilePicker(parent, inputRequest) {
    const self = this
    const fileContainer = document.createElement('div'); fileContainer.classList.add('ask-file-picker', 'ask-date-picker')
    const fileInput = document.createElement('input'); fileInput.type = 'file'; fileInput.style.cssText = 'position:absolute;opacity:0;pointer-events:none;'
    if (inputRequest && inputRequest.constraints) {
      const c = inputRequest.constraints
      const types = Array.isArray(c.accepted_types) ? c.accepted_types : (c.accepted_types ? String(c.accepted_types).split(/\s*,\s*/) : [])
      if (types.length > 0) fileInput.accept = types.join(',')
      const hintParts = []
      if (types.length > 0) hintParts.push('Accepted: ' + types.join(', '))
      if (c.max_size_bytes) hintParts.push('Max size: ' + (c.max_size_bytes / (1024 * 1024)).toFixed(1) + ' MB')
      if (hintParts.length > 0) { const hint = document.createElement('div'); hint.classList.add('ask-file-hint'); hint.textContent = hintParts.join(' · '); fileContainer.appendChild(hint) }
    }
    const fileBtn = document.createElement('div'); fileBtn.classList.add('ask-file-btn', 'ask-action-btn'); fileBtn.textContent = 'Upload file'
    fileBtn.addEventListener('click', () => { if (!fileBtn.classList.contains('disabled')) fileInput.click() })
    fileInput.addEventListener('change', function() { if (!fileInput.files || !fileInput.files[0]) return; const file = fileInput.files[0]; fileBtn.classList.add('disabled'); fileBtn.textContent = file.name; self.handleFileSubmit(file) })
    fileContainer.appendChild(fileInput); fileContainer.appendChild(fileBtn)
    parent.appendChild(fileContainer)
  }

  handleOptionClick(option, container) {
    container.querySelectorAll('.single-choice-option').forEach(btn => { btn.classList.add(btn.dataset.optionId === String(option.id) ? 'selected' : 'disabled') })
    this.addOutgoingMessage(option.label)
    const botMsg = this.chatbotMessage
    const url = botMsg.dataset.url, botId = botMsg.dataset.botId, botUserId = botMsg.dataset.botUserId
    const convId = this.getConversationId()
    const history = JSON.parse(window.sessionStorage.getItem(convId) || '[]')
    for (let i = history.length - 1; i >= 0; i--) { if (history[i].direction === 'incoming' && history[i].options) { history[i].selectedOptionId = option.id; break } }
    window.sessionStorage.setItem(convId, JSON.stringify(history))
    const skipBlocking = this.waitingForWorkflowInput; this.waitingForWorkflowInput = false
    this.sendMessage(url, botId, botUserId, option.id, null, skipBlocking, option.label)
  }

  handleMultipleChoiceSubmit(selectedIds, options) {
    const labels = selectedIds.map(id => { const opt = options.find(o => o.id === id); return opt ? opt.label : id })
    this.addOutgoingMessage(labels.join(', '))
    const botMsg = this.chatbotMessage
    const url = botMsg.dataset.url, botId = botMsg.dataset.botId, botUserId = botMsg.dataset.botUserId
    const convId = this.getConversationId()
    const history = JSON.parse(window.sessionStorage.getItem(convId) || '[]')
    for (let i = history.length - 1; i >= 0; i--) { if (history[i].direction === 'incoming' && history[i].options) { history[i].selectedOptionIds = selectedIds; history[i].selectedOptionId = selectedIds.join(','); break } }
    window.sessionStorage.setItem(convId, JSON.stringify(history))
    const skipBlocking = this.waitingForWorkflowInput; this.waitingForWorkflowInput = false
    this.sendMessage(url, botId, botUserId, selectedIds.join(','), null, skipBlocking, labels.join(', '))
  }

  handleDateSubmit(dateValue) {
    const skipBlocking = this.waitingForWorkflowInput; this.waitingForWorkflowInput = false
    if (!skipBlocking) this.disableSendMessage()
    this.addOutgoingMessage(dateValue)
    const botMsg = this.chatbotMessage
    const url = botMsg.dataset.url, botId = botMsg.dataset.botId, botUserId = botMsg.dataset.botUserId
    const convId = this.getConversationId()
    const history = JSON.parse(window.sessionStorage.getItem(convId) || '[]')
    for (let i = history.length - 1; i >= 0; i--) { if (history[i].direction === 'incoming' && history[i].nodeType === 'ask_date') { history[i].selectedDate = dateValue; break } }
    window.sessionStorage.setItem(convId, JSON.stringify(history))
    this.sendMessage(url, botId, botUserId, dateValue, null, skipBlocking)
  }

  handleFileSubmit(file) {
    this.addOutgoingMessage(file.name)
    const botMsg = this.chatbotMessage
    const uploadUrl = botMsg.dataset.uploadUrl
    const url = botMsg.dataset.url, botId = botMsg.dataset.botId, botUserId = botMsg.dataset.botUserId
    const self = this
    if (!uploadUrl) { this.sendMessage(url, botId, botUserId, file.name); return }
    const formData = new FormData(); formData.append('file', file)
    $.ajax({
      url: uploadUrl, method: 'POST', data: formData, processData: false, contentType: false, dataType: 'json',
      headers: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content') },
      success(response) { if (response.success && response.attachment) self.sendMessage(url, botId, botUserId, file.name, [response.attachment]); else self.sendMessage(url, botId, botUserId, file.name) },
      error() { self.sendMessage(url, botId, botUserId, file.name) }
    })
  }

  // ---- History ----

  putHistoryToConversation() {
    for (const message of this.getMessagesHistory()) {
      if (message.direction === 'incoming') {
        this.addIncomingMessage(message.message, message.sources, message.options, message.nodeType, message.inputRequest)
        const lastIncoming = this.conversationBody.querySelector('.incoming:first-child')
        if (lastIncoming && message.selectedOptionIds) {
          lastIncoming.querySelectorAll('.multiple-choice-option').forEach(row => { row.classList.add('disabled'); if (message.selectedOptionIds.includes(row.dataset.optionId)) { row.classList.add('checked'); const cb = row.querySelector('.multiple-choice-checkbox'); if (cb) cb.innerHTML = this.checkedCheckboxSvg() } })
          const sub = lastIncoming.querySelector('.multiple-choice-submit'); if (sub) sub.classList.add('disabled')
        }
        if (lastIncoming && message.selectedOptionId) { lastIncoming.querySelectorAll('.single-choice-option').forEach(btn => { btn.classList.add(btn.dataset.optionId === String(message.selectedOptionId) ? 'selected' : 'disabled') }) }
        if (lastIncoming && message.selectedDate) {
          const dateBtn = lastIncoming.querySelector('.ask-date-btn'), datePicker = lastIncoming.querySelector('.ask-date-picker'), datetimeRow = lastIncoming.querySelector('.ask-datetime-row')
          if (dateBtn) { dateBtn.textContent = message.selectedDate; dateBtn.style.display = '' }
          if (datetimeRow) datetimeRow.style.display = 'none'
          if (datePicker) datePicker.classList.add('disabled')
        }
      } else if (message.direction === 'outgoing') { this.addOutgoingMessage(message.message) }
    }
    const history = this.getMessagesHistory()
    const lastEntry = history[history.length - 1]
    if (lastEntry && lastEntry.direction === 'incoming' && lastEntry.options && lastEntry.options.length > 0 && !lastEntry.selectedOptionId) {
      this.disableSendMessage()
      $(".chatbot-interactive-reset").attr("disabled", false).removeClass("disabled")
      $(".conversation-control").removeClass("disabled")
    }
  }

  getMessagesHistory() { return JSON.parse(window.sessionStorage.getItem(this.getConversationId()) || '[]') }
  addMessageToHistory(message, sources, direction, options, nodeType, inputRequest) {
    if (!message) return
    const history = this.getMessagesHistory()
    history.push({ message, sources, direction, options: options || null, nodeType: nodeType || null, inputRequest: inputRequest || null })
    window.sessionStorage.setItem(this.getConversationId(), JSON.stringify(history))
  }
  getSessionId() {
    let id = window.sessionStorage.getItem("conversation_id")
    if (!id) { id = this.uuid(); window.sessionStorage.setItem("conversation_id", id) }
    return id
  }
  getConversationId() {
    const botMsg = this.chatbotMessage
    return botMsg ? botMsg.dataset.botId + ":" + this.getSessionId() : this.getSessionId()
  }
  getMessageOrderNumber() { return ++this.messageOrder }

  // ---- Widget preview ----

  updateWidgetColorPreview() {
    const color = $("#widget_color").val()
    if (!color || !color.match(/^\#([a-zA-Z0-9]){6}$/)) return
    $(".conversation-header").css({ "border-color": color, "background-color": color })
    $(".outgoing .msg").css({ "border-color": color, "background-color": color })
    $(".msg-input-wrap").css("border-color", color)
    $(".icon-send").css("color", color); $(".icon-reset").css("color", color)
    $(".chatbot-interactive-reset").css("border-color", color)
    $(".button-logo-wrapper").css("background-color", color)
    $(".chatbot-interactive-voice").css("border-color", color)
    $(".icon-mic").css("color", color); $(".icon-stop").css("color", color)
  }

  initWidgetLogoButtonHandler() {
    $(this.element).on('click.convTester', '.button-logo-wrapper', function (e) {
      e.preventDefault()
      const dialog = document.getElementsByClassName('conversation-content-wrapper')[0]
      if (dialog.style.display === "none") {
        this.classList.remove("closed"); dialog.style.display = "flex"
        this.innerHTML = '<i class="icon-criss-cross"></i>'
      } else {
        this.classList.add("closed"); dialog.style.display = "none"
        const logo = document.createElement('img'); logo.classList.add('button-logo'); logo.src = this.dataset.buttonLogoUrl
        this.innerHTML = ''; this.appendChild(logo)
      }
    })
  }

  initHandleSettingsBtnClick() {
    $('#settings-btn').on('click.convTester', function (e) { e.preventDefault(); document.getElementById('dropdown-content').classList.toggle('show') })
  }

  initHandleAudioFeatureBtnClick() {
    $('#audio-features').on('click.convTester', function (e) {
      e.preventDefault()
      const recordingBtn = document.getElementsByClassName('chatbot-interactive-voice')[0]
      if (!recordingBtn) return
      if (this.textContent === 'Enable Voice Chat') { this.textContent = 'Disable Voice Chat'; recordingBtn.style.display = 'flex' }
      else { this.textContent = 'Enable Voice Chat'; recordingBtn.style.display = 'none' }
    })
  }

  // ---- Widget sizing ----

  calculateHeadersHeight() {
    let totalHeight = 0
    const breadcrumbs = document.querySelector(".breadcrumb-wrap")
    document.querySelectorAll("nav").forEach(header => {
      const rect = header.getBoundingClientRect()
      totalHeight += Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0))
    })
    if (breadcrumbs) { const rect = breadcrumbs.getBoundingClientRect(); totalHeight += Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)) }
    return totalHeight
  }

  initTestWidgetScrollHandler() {
    const chatContainer = this.element.querySelector('.conversation-fixed-block')
    if (!chatContainer) return
    chatContainer.style.width = chatContainer.offsetWidth + "px"
    chatContainer.style.transition = 'top 0.1s ease-out'
    window.addEventListener('scroll', this.boundTestWidgetScroll)
  }

  updateTestWidgetPosition() {
    const chatContainer = this.element.querySelector('.conversation-fixed-block')
    if (!chatContainer) return
    const topHeight = this.calculateHeadersHeight()
    if (topHeight === 0) {
      if (chatContainer.style.position !== 'fixed') { chatContainer.style.position = 'fixed'; chatContainer.style.top = '13px' }
    } else if (chatContainer.style.position !== 'sticky') {
      chatContainer.style.top = ''
      chatContainer.style.position = 'sticky'
    }
  }

  setWidgetSize() {
    const chatContainer = this.element.querySelector('.conversation-fixed-block')
    if (!chatContainer) return
    const minW = 400, maxW = 600, minH = 480, maxH = 800
    chatContainer.style.width = `${Math.min(maxW, Math.max(minW, window.innerWidth * 0.33))}px`
    const headerHeight = this.calculateHeadersHeight()
    const style = window.getComputedStyle(chatContainer)
    const margins = parseFloat(style.marginTop) + parseFloat(style.marginBottom)
    let chatHeight = window.innerHeight - margins
    if (style.position === 'fixed') chatHeight -= headerHeight
    chatHeight = Math.min(maxH, Math.max(minH, chatHeight))
    chatContainer.style.height = `${chatHeight}px`
    if (style.position === 'fixed') chatContainer.style.top = `${headerHeight + 13}px`
    chatContainer.style.visibility = "visible"
  }

  // ---- Helpers ----

  parseAndSanitize(text) {
    if (!this.marked || !this.purify) return text
    return this.purify.sanitize(this.marked.parse(text), {
      ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'p', 'div', 'span', 'code', 'pre', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br'],
      ALLOWED_ATTR: ['href', 'target', 'class', 'style', 'rel']
    })
  }

  convertNewlinesToTextNodes(text) {
    const lines = text.split('\n'), frag = document.createDocumentFragment()
    for (let i = 0; i < lines.length; i++) { frag.appendChild(document.createTextNode(lines[i])); if (i < lines.length - 1) frag.appendChild(document.createElement('br')) }
    return frag
  }

  uuid() { return ('10000000-1000-4000-8000-100000000000').replace(/[018]/g, c => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)) }

  checkedCheckboxSvg() { return '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="19" height="19" rx="3.5" fill="var(--current-widget-color)" stroke="var(--current-widget-color)"/><path d="M15 6.5L8.5 13L5 9.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' }
  uncheckedCheckboxSvg() { return '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="19" height="19" rx="3.5" fill="white" stroke="#D9E3F7"/></svg>' }
}
